import React from "react";
import { motion } from "framer-motion";
import { Award, Sparkles, Users2 } from "lucide-react";
import { TOWNHALL_RECOGNITIONS } from "../data/content";

// Townhall recognitions - three award-winner cards + a group photo band + founder note.
// Data-driven; add/replace awardees in data/content.js as future townhalls happen.
const TownhallRecognition = () => {
    const { eyebrow, heading, intro, awardees, group, founderNote } = TOWNHALL_RECOGNITIONS;

    return (
        <section className="relative py-14 lg:py-20" data-testid="townhall-recognition">
            <div className="max-w-7xl mx-auto px-5 lg:px-10">
                {/* Header */}
                <div className="mb-10 lg:mb-14 max-w-3xl">
                    <div className="pill" data-testid="townhall-eyebrow">
                        <span className="accent-dot" />{eyebrow}
                    </div>
                    <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl text-ivory tracking-tight leading-[1.05]">
                        {heading.split("brand ambassador")[0]}
                        <span className="italic text-gold-shimmer">brand ambassador</span>
                        {heading.split("brand ambassador")[1]}
                    </h2>
                    <p className="mt-5 text-ivory/70 leading-relaxed text-sm lg:text-base">{intro}</p>
                </div>

                {/* Awardee cards */}
                <div className="grid md:grid-cols-3 gap-4 lg:gap-5">
                    {awardees.map((a, i) => (
                        <motion.article
                            key={a.id}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                            className="glass rounded-2xl overflow-hidden flex flex-col"
                            data-testid={`awardee-${a.id}`}
                        >
                            {/* Photo with gold frame */}
                            <div className="relative aspect-[4/5] overflow-hidden bg-gradient-to-br from-[#1A1A22] to-obsidian">
                                <img
                                    src={a.image}
                                    alt={`${a.name} - ${a.award}`}
                                    className="absolute inset-0 w-full h-full object-cover object-top"
                                    loading="lazy"
                                />
                                {/* Badge pill */}
                                <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-obsidian/85 backdrop-blur-md border border-gold/40 text-gold text-[9px] font-mono uppercase tracking-widest">
                                    <Award className="w-3 h-3" />
                                    {a.badge}
                                </div>
                                {a.firstEver && (
                                    <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gold text-obsidian text-[9px] font-mono uppercase tracking-widest font-bold" data-testid={`awardee-first-${a.id}`}>
                                        <Sparkles className="w-3 h-3" /> First ever
                                    </div>
                                )}
                                {/* Gold gradient overlay at bottom */}
                                <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-obsidian/95 via-obsidian/40 to-transparent" />
                            </div>

                            {/* Card body */}
                            <div className="p-6 flex flex-col flex-1">
                                <div className="font-mono text-[10px] uppercase tracking-widest text-gold/85">{a.award} · {a.period}</div>
                                <h3 className="mt-2 font-display text-2xl text-ivory leading-tight">{a.name}</h3>
                                <p className="mt-3 text-sm text-ivory/60 leading-relaxed flex-1">{a.citation}</p>
                            </div>
                        </motion.article>
                    ))}
                </div>

                {/* Group photos */}
                <div className="mt-8 lg:mt-10 grid sm:grid-cols-2 gap-4 lg:gap-5">
                    {group.map((g, i) => (
                        <motion.div
                            key={g.id}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.55, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                            className="relative aspect-video overflow-hidden rounded-2xl border border-gold/15 bg-gradient-to-br from-[#1A1A22] to-obsidian"
                            data-testid={g.id}
                        >
                            <img
                                src={g.src}
                                alt={g.alt}
                                className="absolute inset-0 w-full h-full object-cover"
                                loading="lazy"
                            />
                        </motion.div>
                    ))}
                </div>

                {/* Founder note */}
                <motion.blockquote
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.6, delay: 0.15 }}
                    className="mt-10 lg:mt-14 max-w-3xl rounded-2xl p-6 lg:p-8 flex items-start gap-4"
                    style={{
                        background: "linear-gradient(135deg, rgba(212,168,87,0.06), rgba(20,20,26,0.6))",
                        border: "1px solid rgba(212,168,87,0.22)",
                    }}
                    data-testid="townhall-founder-note"
                >
                    <div className="w-10 h-10 rounded-xl bg-gold/10 border border-gold/25 flex items-center justify-center shrink-0">
                        <Users2 className="w-5 h-5 text-gold" />
                    </div>
                    <p className="text-ivory/80 leading-relaxed italic text-sm lg:text-base">
                        {founderNote}
                    </p>
                </motion.blockquote>
            </div>
        </section>
    );
};

export default TownhallRecognition;
